<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import WootMessageEditor from '../../../../components/widgets/WootWriter/Editor.vue';
import WootInput from '../../../../../v3/components/Form/Input.vue';
import WootToggle from './Toggle.vue';
import WootSelect from '../../../../../v3/components/Form/Select.vue';
import WootButton from '../../../../components/ui/WootButton.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import PipelinesAPI from 'dashboard/api/pipelines';

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({}),
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['saved', 'close']);
const { t } = useI18n();
const store = useStore();
const isLoading = ref(false);
const errors = ref({});
const pipelineCards = ref([]);

// Estado para controlar quando os dados estão prontos
const isDataReady = ref(false);

// Buscar todos os funis ao montar o componente
onMounted(async () => {
  try {
    isLoading.value = true;
    // Dispara a action para buscar todos os funis
    await store.dispatch('pipelines/fetch');
  } catch (error) {
    // Error handled silently
  } finally {
    isLoading.value = false;
  }
});

// Computed para obter todos os funis
const pipelines = computed(() => store.getters['pipelines/getPipelines']);

// Computed para obter o funil selecionado
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);

// Função para garantir que o valor é uma string
const ensureString = value => (value ? String(value) : '');

// Computed para obter as etapas do funil selecionado
const pipelineStages = computed(() => {
  // Busca o funil diretamente dos records usando o ID atual
  const currentPipeline = pipelines.value.find(
    // eslint-disable-next-line no-use-before-define -- formData declared lower in setup, evaluated lazily
    f => String(f.id) === formData.value.pipeline_id
  );

  if (!currentPipeline?.stages) return [];

  const stages = Object.entries(currentPipeline.stages)
    .map(([id, stage]) => ({
      value: String(id),
      label: stage.name,
      position: stage.position,
    }))
    .sort((a, b) => a.position - b.position);

  return stages;
});

// Computed para transformar os funis em options para o select
const pipelineOptions = computed(() => {
  return pipelines.value.map(pipeline => ({
    value: String(pipeline.id),
    label: pipeline.name,
  }));
});

// Form data atualizado para incluir o funil_id
const formData = ref({
  pipeline_id: ensureString(props.initialData.pipeline_id),
  stage_id: ensureString(props.initialData.stage_id),
  title: props.initialData.title || '',
  content: props.initialData.content || '',
  webhook: {
    enabled: props.initialData.webhook?.enabled || false,
    url: props.initialData.webhook?.url || '',
    method: props.initialData.webhook?.method || 'POST',
  },
  conditions: {
    enabled: props.initialData.conditions?.enabled || false,
    rules: props.initialData.conditions?.rules || [],
  },
});

// Validation
const validateForm = () => {
  errors.value = {};

  if (!selectedPipeline.value?.id) {
    errors.value.pipeline = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.PIPELINE_REQUIRED'
    );
  }

  if (!formData.value.stage_id) {
    errors.value.stage = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.STAGE_REQUIRED'
    );
  }

  if (!formData.value.title.trim()) {
    errors.value.title = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.TITLE_REQUIRED'
    );
  }

  if (!formData.value.content.trim()) {
    errors.value.content = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.CONTENT_REQUIRED'
    );
  }

  if (formData.value.webhook.enabled && !formData.value.webhook.url.trim()) {
    errors.value.webhookUrl = t(
      'PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.WEBHOOK_URL_REQUIRED'
    );
  }

  return Object.keys(errors.value).length === 0;
};

// Atualiza o handler de submit para incluir o funil e etapa
const handleSubmit = async () => {
  try {
    if (!validateForm()) return;

    isLoading.value = true;
    const currentPipeline = pipelines.value.find(
      f => String(f.id) === formData.value.pipeline_id
    );

    if (!currentPipeline) {
      throw new Error(t('PIPELINE.MESSAGES.FUNNEL_NOT_FOUND'));
    }

    // Cria uma cópia do funil mantendo a estrutura original
    const updatedPipeline = {
      ...currentPipeline,
      stages: { ...currentPipeline.stages }, // Clona stages para preservar IDs
    };

    const selectedStageId = formData.value.stage_id;
    const currentStage = updatedPipeline.stages[selectedStageId];

    // Preserva a estrutura existente da etapa
    updatedPipeline.stages[selectedStageId] = {
      ...currentStage,
      message_templates: [...(currentStage?.message_templates || [])],
    };

    if (props.isEditing) {
      const templateIndex = updatedPipeline.stages[
        selectedStageId
        // eslint-disable-next-line no-shadow -- inner t() callback param shadow is a deliberate iteration var
      ].message_templates.findIndex(t => t.id === props.initialData.id);

      if (templateIndex > -1) {
        updatedPipeline.stages[selectedStageId].message_templates[
          templateIndex
        ] = {
          ...props.initialData,
          ...formData.value,
          updated_at: new Date().toISOString(),
        };
      }
    } else {
      updatedPipeline.stages[selectedStageId].message_templates.push({
        id: Date.now(),
        ...formData.value,
        created_at: new Date().toISOString(),
      });
    }

    await PipelinesAPI.update(currentPipeline.id, updatedPipeline);
    await store.dispatch('pipelines/fetch', { force: true });
    emit('saved', formData.value);
  } catch (error) {
    errors.value = {
      submit: t('PIPELINE.MESSAGE_TEMPLATES.FORM.ERRORS.SAVE_ERROR'),
    };
  } finally {
    isLoading.value = false;
  }
};

// Busca os itens do Pipeline para extrair os campos disponíveis
const fetchPipelineCards = async () => {
  try {
    isLoading.value = true;
    const currentPipeline = store.getters['pipelines/getSelectedPipeline'];

    if (!currentPipeline?.id) {
      return;
    }

    const response = await PipelineCardsAPI.getCards(currentPipeline.id);

    // Handle paginated response format (data.data) vs legacy (data as array)
    const items = response.data?.data || response.data;

    pipelineCards.value = items.map(item => ({
      id: item.id,
      title: item.item_details.title || '',
      description: item.item_details.description || '',
      priority: item.item_details.priority || 'none',
      assignee: item.item_details.assignee || null,
      position: item.position,
      pipeline_stage: item.pipeline_stage,
      item_details: item.item_details,
      custom_attributes: item.custom_attributes || {},
      createdAt: new Date(item.created_at).toLocaleDateString(),
    }));
  } catch (error) {
    // Error handled silently
  } finally {
    isLoading.value = false;
  }
};

// Computed para extrair campos únicos dos itens
const availableFields = computed(() => {
  return [
    { value: 'item_details.title', label: t('PIPELINE.VARIABLES.TITLE') },
    {
      value: 'item_details.description',
      label: t('PIPELINE.VARIABLES.DESCRIPTION'),
    },
    { value: 'item_details.priority', label: t('PIPELINE.VARIABLES.PRIORITY') },
    { value: 'item_details.agent_id', label: t('PIPELINE.VARIABLES.AGENT') },
    {
      value: 'item_details.conversation_id',
      label: t('PIPELINE.VARIABLES.CONVERSATION'),
    },
    { value: 'item_details.value', label: t('PIPELINE.VARIABLES.VALUE') },
    { value: 'pipeline_stage', label: t('PIPELINE.STAGE.LABEL') },
  ].sort((a, b) => a.label.localeCompare(b.label));
});

// Computed para o v-model do pipeline_id
const selectedPipelineId = computed({
  get: () => {
    return formData.value.pipeline_id;
  },
  set: async value => {
    formData.value.pipeline_id = ensureString(value);
    // eslint-disable-next-line no-use-before-define -- handlePipelineChange declared below; setup evaluates lazily
    await handlePipelineChange(value);
  },
});

// Computed para o v-model do stage_id
const selectedStageId = computed({
  get: () => {
    return formData.value.stage_id;
  },
  set: value => {
    formData.value.stage_id = ensureString(value);
  },
});

// Available HTTP methods for webhook
const httpMethods = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
];

// Computed para controlar a exibição do editor de regras condicionais
const showConditionsEditor = computed(() => formData.value.conditions.enabled);

const addConditionRule = () => {
  formData.value.conditions.rules.push({
    field: '',
    operator: 'equals',
    value: '',
  });
};

const removeConditionRule = index => {
  formData.value.conditions.rules.splice(index, 1);
};

// Handler para quando um funil é selecionado
const handlePipelineChange = async value => {
  try {
    // Atualiza o ID do funil no formData
    formData.value.pipeline_id = ensureString(value);

    // Busca o funil completo dos records do store
    // eslint-disable-next-line no-shadow -- intentionally shadows the outer ref to keep this scope local
    const selectedPipeline = pipelines.value.find(
      f => String(f.id) === formData.value.pipeline_id
    );

    if (selectedPipeline) {
      // Atualiza o funil selecionado no store
      await store.dispatch('pipelines/setSelectedPipeline', selectedPipeline);

      await nextTick();

      // Só limpa a etapa se não estiver editando ou se mudar o funil
      if (
        !props.isEditing ||
        formData.value.pipeline_id !==
          ensureString(props.initialData.pipeline_id)
      ) {
        const stages = Object.keys(selectedPipeline.stages || {});
        formData.value.stage_id = stages[0] || '';
      }
      // Keep existing stage_id if pipeline hasn't changed
    }
  } catch (error) {
    // Error handled silently
  }
};

// Carrega os itens quando o componente é montado
onMounted(async () => {
  try {
    isLoading.value = true;

    // Busca todos os funis
    await store.dispatch('pipelines/fetch');

    // Aguarda o DOM atualizar
    await nextTick();

    // Inicializa o formData com os valores convertidos para string
    formData.value = {
      ...formData.value,
      pipeline_id: ensureString(props.initialData.pipeline_id),
      stage_id: ensureString(props.initialData.stage_id),
    };

    // Se houver um funil_id inicial, seleciona ele
    if (props.initialData.pipeline_id) {
      const pipeline = pipelines.value.find(
        f => String(f.id) === ensureString(props.initialData.pipeline_id)
      );

      if (pipeline) {
        // Atualiza o funil selecionado no store
        await store.dispatch('pipelines/setSelectedPipeline', pipeline);

        // Verifica se a stage_id existe nas stages do funil
        const stages = Object.keys(pipeline.stages || {});

        const initialStageId = ensureString(props.initialData.stage_id);

        const stageExists = stages.some(id => String(id) === initialStageId);

        // Aguarda o DOM atualizar
        await nextTick();

        // Força a atualização dos selects
        if (!stageExists) {
          formData.value.stage_id = stages[0] || '';
        }
      }
    }

    await fetchPipelineCards();
    await nextTick();
    isDataReady.value = true;
  } catch (error) {
    // Error loading data
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <form class="space-y-4" @submit.prevent="handleSubmit">
    <h3 class="text-lg font-medium mb-4">
      {{
        t(
          isEditing
            ? 'PIPELINE.MESSAGE_TEMPLATES.EDIT.TITLE'
            : 'PIPELINE.MESSAGE_TEMPLATES.NEW'
        )
      }}
    </h3>
    <div class="grid grid-cols-2 gap-4">
      <!-- Coluna Esquerda -->
      <div class="space-y-4">
        <!-- Seleção de Etapa do Funil -->
        <div class="px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <h3
            class="text-base font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.PIPELINE_INFO') }}
          </h3>

          <!-- Loading state para os funis -->
          <div
            v-if="isLoading"
            class="text-sm text-slate-600 dark:text-slate-400 mb-4"
          >
            {{ t('PIPELINE.LOADING_PIPELINES') }}
          </div>

          <!-- Seletores -->
          <div v-if="isDataReady" class="mb-2">
            <WootSelect
              v-model="selectedPipelineId"
              name="pipeline_id"
              :label="t('PIPELINE.MESSAGE_TEMPLATES.FORM.PIPELINE.LABEL')"
              :placeholder="
                t('PIPELINE.MESSAGE_TEMPLATES.FORM.PIPELINE.PLACEHOLDER')
              "
              :options="pipelineOptions"
              :error="errors.pipeline"
              :value="formData.pipeline_id"
            />

            <WootSelect
              v-model="selectedStageId"
              name="stage_id"
              :label="t('PIPELINE.MESSAGE_TEMPLATES.FORM.STAGE.LABEL')"
              :placeholder="
                t('PIPELINE.MESSAGE_TEMPLATES.FORM.STAGE.PLACEHOLDER')
              "
              :options="pipelineStages"
              :error="errors.stage"
              class="mb-2"
              :value="formData.stage_id"
            />
          </div>
        </div>

        <!-- Informações Básicas (apenas título) -->
        <div class="px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <h3
            class="text-base font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.BASIC_INFO') }}
          </h3>

          <WootInput
            v-model="formData.title"
            name="title"
            :label="t('PIPELINE.MESSAGE_TEMPLATES.FORM.TITLE.LABEL')"
            :placeholder="
              t('PIPELINE.MESSAGE_TEMPLATES.FORM.TITLE.PLACEHOLDER')
            "
            :error="errors.title"
            class="mb-4"
          />
        </div>

        <!-- Configuração de Webhook -->
        <div class="px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div class="flex items-center justify-between mb-4">
            <h3
              class="text-base font-medium text-slate-700 dark:text-slate-300 mb-0"
            >
              {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.WEBHOOK.TITLE') }}
            </h3>
            <WootToggle v-model="formData.webhook.enabled" />
          </div>

          <div v-if="formData.webhook.enabled" class="mt-4">
            <WootInput
              v-model="formData.webhook.url"
              name="webhook_url"
              :label="t('PIPELINE.MESSAGE_TEMPLATES.FORM.WEBHOOK.URL.LABEL')"
              :placeholder="
                t('PIPELINE.MESSAGE_TEMPLATES.FORM.WEBHOOK.URL.PLACEHOLDER')
              "
              :error="errors.webhookUrl"
            />

            <WootSelect
              v-model="formData.webhook.method"
              name="webhook_method"
              :label="t('PIPELINE.MESSAGE_TEMPLATES.FORM.WEBHOOK.METHOD.LABEL')"
              :options="httpMethods"
            />
          </div>
        </div>

        <!-- Condições -->
        <div class="px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div class="flex items-center justify-between mb-4">
            <h3
              class="text-base font-medium text-slate-700 dark:text-slate-300 mb-0"
            >
              {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.TITLE') }}
            </h3>
            <WootToggle v-model="formData.conditions.enabled" />
          </div>

          <div v-if="showConditionsEditor" class="conditions-config space-y-4">
            <div
              v-for="(rule, index) in formData.conditions.rules"
              :key="index"
              class="condition-rule flex items-start gap-2"
            >
              <div class="flex-1">
                <div class="space-y-2">
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{
                      t(
                        'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.FIELD.LABEL'
                      )
                    }}
                  </label>
                  <select
                    v-model="rule.field"
                    class="w-full pl-3 py-2 border border-n-weak rounded-lg bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-n-brand focus:border-n-brand"
                  >
                    <option value="" disabled>
                      {{
                        t(
                          'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.FIELD.PLACEHOLDER'
                        )
                      }}
                    </option>
                    <option
                      v-for="field in availableFields"
                      :key="field.value"
                      :value="field.value"
                    >
                      {{ field.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="w-32">
                <div class="space-y-2">
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{
                      t(
                        'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.OPERATOR.LABEL'
                      )
                    }}
                  </label>
                  <select
                    v-model="rule.operator"
                    class="w-full pl-3 py-2 border border-n-weak rounded-lg bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-n-brand focus:border-n-brand"
                  >
                    <option value="equals">
                      {{ $t('PIPELINE.TEMPLATE_RULES.OP_EQUALS') }}
                    </option>
                    <option value="contains">
                      {{ $t('PIPELINE.TEMPLATE_RULES.OP_CONTAINS') }}
                    </option>
                    <option value="starts_with">
                      {{ $t('PIPELINE.TEMPLATE_RULES.OP_STARTS_WITH') }}
                    </option>
                    <option value="ends_with">
                      {{ $t('PIPELINE.TEMPLATE_RULES.OP_ENDS_WITH') }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="flex-1">
                <div class="space-y-2">
                  <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{
                      t(
                        'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.VALUE.LABEL'
                      )
                    }}
                  </label>
                  <input
                    v-model="rule.value"
                    type="text"
                    class="w-full pl-3 py-2 border border-n-weak rounded-lg bg-n-solid-2 text-n-slate-12 focus:ring-1 focus:ring-n-brand focus:border-n-brand"
                    :placeholder="
                      t(
                        'PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.VALUE.PLACEHOLDER'
                      )
                    "
                  />
                </div>
              </div>
              <WootButton
                variant="clear"
                color-scheme="secondary"
                size="small"
                @click="removeConditionRule(index)"
              >
                <FluentIcon icon="delete" size="16" :icons="dashboardIcons" />
              </WootButton>
            </div>

            <WootButton
              variant="clear"
              size="small"
              class="w-full"
              @click="addConditionRule"
            >
              <FluentIcon
                icon="add"
                size="16"
                class="mr-2"
                :icons="dashboardIcons"
              />
              {{ t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONDITIONS.ADD_RULE') }}
            </WootButton>
          </div>
        </div>
      </div>

      <!-- Coluna Direita (Editor) -->
      <div class="sticky top-0 border border-dashed border-woot-500 rounded-lg">
        <!-- eslint-disable vue/no-static-inline-styles -->
        <!-- viewport math (calc(100vh - 400px)) cannot be expressed in tailwind tokens -->
        <div
          class="h-full bg-slate-50 dark:bg-slate-800 rounded-lg p-4"
          style="min-height: calc(100vh - 400px)"
        >
          <!-- eslint-enable vue/no-static-inline-styles -->
          <WootMessageEditor
            v-model="formData.content"
            :placeholder="
              t('PIPELINE.MESSAGE_TEMPLATES.FORM.CONTENT.PLACEHOLDER')
            "
            :error="errors.content"
            class="h-full"
          />
        </div>
      </div>
    </div>

    <!-- Botões de Ação -->
    <div class="flex justify-end gap-2 pt-4">
      <WootButton
        variant="clear"
        color-scheme="secondary"
        @click="emit('close')"
      >
        {{ t('PIPELINE.ACTIONS.CANCEL') }}
      </WootButton>
      <WootButton variant="primary" type="submit">
        {{ t('PIPELINE.ACTIONS.SAVE') }}
      </WootButton>
    </div>
  </form>
</template>

<style scoped>
/* Editor deep styling */
:deep(.woot-editor) {
  border: 1px dashed rgb(var(--color-woot-500));
  border-radius: 0.5rem;
  padding: 0.5rem;
  background-color: white;
  height: calc(100vh - 450px);
}

.dark :deep(.woot-editor) {
  background-color: rgb(51 65 85); /* slate-700 */
}
</style>
